import cep from 'cep-promise';
import type React from 'react';
import { useEffect, useState, type FormEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FormInputNameRegister } from 'src/features/core/auth/types';
import { updateMask as updateMaskCEP } from 'src/helpers/masks/cep';
import { unmask } from 'src/helpers/masks/generalDate';
import validators from 'src/helpers/validators';
import useForm from 'src/hooks/useForm';
import type Client from 'src/model/User';
import type { UserBasic } from 'src/model/UserBasic';
import { setLoading, useLoading } from 'src/redux/loading/loadingSlice';
import { api } from 'src/services/api';
import type { Address } from './../../model/Address';
import { AddressManagerUI } from './ui';

interface AddressManagerScreenProps {
  address: Address[];
}

export const AddressManagerScreen: React.FC<AddressManagerScreenProps> = ({
  address,
}): JSX.Element => {
  const { loading } = useSelector(useLoading);
  const dispatch = useDispatch();
  const [addresses, setAddresses] = useState<Address[]>(address);
  useEffect(() => {
    setAddresses(address);
  }, [address]);

  const normalize = (s: string) =>
    (s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();

  const validateCepCityState = async (
    zipCode: string,
    state: string,
    city: string
  ) => {
    // const raw = unmask(zipCode) ?? '';
    // console.log(raw);
    // if (raw.length !== 8) return { ok: false, reason: 'CEP inválido' as const };
    const data = await cep(zipCode);
    console.log(data);
    const ok =
      normalize(data.state) === normalize(state) &&
      normalize(data.city) === normalize(city);
    console.log(normalize(data.city), normalize(city));
    console.log(normalize(data.state), normalize(state));
    return { ok, expected: { state: data.state, city: data.city } };
  };

  const ensureCepMatches = async () => {
    const zipCode = formDataRegister[FormInputNameRegister.zipCode];
    const state = formDataRegister[FormInputNameRegister.state];
    const city = formDataRegister[FormInputNameRegister.city];
    const result = await validateCepCityState(zipCode, state, city);
    console.log(result);
    if (!result.ok) {
      setErrorRegister({
        state: ['Estado não corresponde ao CEP'],
        city: ['Cidade não corresponde ao CEP'],
      });
      // if (result.reason === 'CEP inválido') toast.error('CEP inválido');
      // else
      //   toast.error(
      //     `CEP não confere. Esperado: ${result.expected.city}/${result.expected.state}`
      //   );
      return false;
    }
    return true;
  };

  const {
    formData: formDataRegister,
    formErrors: formErrorRegister,
    setErrors: setErrorRegister,
    onChangeFormInput: onChangeFormInputRegister,
    isFormValid: isFormValidRegister,
    resetForm: resetFormRegister,
  } = useForm({
    initialData: {
      zipCode: '',
      state: '',
      city: '',
      street: '',
      complement: '',
      number: '',
      neighborhood: '',
      imageBase64: '',
      country: '',
      main: 'false',
    },
    validators: {
      zipCode: [validators.required],
      state: [validators.required],
      city: [validators.required],
      street: [validators.required],
      number: [validators.required],
      country: [validators.required],
      neighborhood: [validators.required],
    },
    formatters: {
      zipCode: updateMaskCEP,
    },
  });

  const handleEditAddress = async (id: string, e: FormEvent): Promise<void> => {
    console.log(id);
    try {
      e.preventDefault();
      if (isFormValidRegister()) {
        dispatch(setLoading(true));

        const ok = await ensureCepMatches();
        if (!ok) return;

        const payload: Address = {
          id: id,
          zipcode: formDataRegister[FormInputNameRegister.zipCode],
          state: formDataRegister[FormInputNameRegister.state],
          city: formDataRegister[FormInputNameRegister.city],
          district: formDataRegister[FormInputNameRegister.neighborhood],
          street: formDataRegister[FormInputNameRegister.street],
          complement: formDataRegister[FormInputNameRegister.complement],
          number: formDataRegister[FormInputNameRegister.number],
          country: formDataRegister[FormInputNameRegister.country],
          main: formDataRegister[FormInputNameRegister.main] === 'true',
        };

        const response = await api.put<Address>(
          '/user/update-address',
          payload
        );

        if (response.status === 200) {
          resetFormRegister();
          toast.success('Endereço criado com sucesso!');
          const newAddress = response.data;
          setAddresses((prev) => [...prev, newAddress]);
          window.location.reload();
        } else if (response.status === 400) {
          const errorMessage =
            typeof response.data === 'string'
              ? response.data
              : JSON.stringify(response.data);
          toast.error(errorMessage);
        }
      }
      // eslint-disable-next-line
    } catch (error: any) {
      let errorMessage =
        'Falha ao realizar o cadastro, tente novamente mais tarde';
      if (error.response && error.response.data) {
        errorMessage =
          typeof error.response.data === 'string'
            ? error.response.data
            : JSON.stringify(error.response.data);
      }
      toast.error(errorMessage);
      setErrorRegister({
        document: [errorMessage],
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleOnSubmitRegister = async (e: FormEvent): Promise<void> => {
    try {
      e.preventDefault();
      if (isFormValidRegister()) {
        dispatch(setLoading(true));
        const ok = await ensureCepMatches();
        if (!ok) return;

        const payload: Address = {
          zipcode: formDataRegister[FormInputNameRegister.zipCode],
          state: formDataRegister[FormInputNameRegister.state],
          city: formDataRegister[FormInputNameRegister.city],
          district: formDataRegister[FormInputNameRegister.neighborhood],
          street: formDataRegister[FormInputNameRegister.street],
          complement: formDataRegister[FormInputNameRegister.complement],
          number: formDataRegister[FormInputNameRegister.number],
          country: formDataRegister[FormInputNameRegister.country],
          main: formDataRegister[FormInputNameRegister.main] === 'true',
        } as Address;

        const response = await api.post<Client>(
          '/user/create-address/',
          payload
        );

        if (response.status === 201) {
          resetFormRegister();
          toast.success('Dados salvos com sucesso!');
          window.location.reload();
        } else if (response.status === 400) {
          const errorMessage =
            typeof response.data === 'string'
              ? response.data
              : JSON.stringify(response.data);
          toast.error(errorMessage);
        }
      }
      // eslint-disable-next-line
    } catch (error: any) {
      let errorMessage =
        'Falha ao realizar o cadastro, tente novamente mais tarde';
      if (error.response && error.response.data) {
        errorMessage =
          typeof error.response.data === 'string'
            ? error.response.data
            : JSON.stringify(error.response.data);
      }
      toast.error(errorMessage);
      setErrorRegister({
        document: [errorMessage],
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleOnChangeCEP = async (value: string): Promise<void> => {
    if (value.length === 9) {
      const cepResponse = await cep(value);
      onChangeFormInputRegister(FormInputNameRegister.state)(cepResponse.state);
      onChangeFormInputRegister(FormInputNameRegister.city)(cepResponse.city);
      onChangeFormInputRegister(FormInputNameRegister.neighborhood)(
        cepResponse.neighborhood
      );
      onChangeFormInputRegister(FormInputNameRegister.street)(
        cepResponse.street
      );
    }
  };

  const handleOnSetAddress = (address: Address): void => {
    onChangeFormInputRegister(FormInputNameRegister.zipCode)(address.zipcode);
    onChangeFormInputRegister(FormInputNameRegister.state)(address.state);
    onChangeFormInputRegister(FormInputNameRegister.city)(address.city);
    onChangeFormInputRegister(FormInputNameRegister.neighborhood)(
      address.district
    );
    onChangeFormInputRegister(FormInputNameRegister.street)(address.street);
    onChangeFormInputRegister(FormInputNameRegister.complement)(
      address.complement
    );
    onChangeFormInputRegister(FormInputNameRegister.number)(address.number);
    onChangeFormInputRegister(FormInputNameRegister.country)(address.country);
    onChangeFormInputRegister(FormInputNameRegister.main)(
      (address.main || false).toString()
    );
  };

  return (
    <AddressManagerUI
      address={address}
      state={loading}
      formData={formDataRegister}
      formErrors={formErrorRegister}
      onChangeFormInput={onChangeFormInputRegister}
      onChangeCEP={handleOnChangeCEP}
      onSubmitRegister={handleOnSubmitRegister}
      onSetAddress={handleOnSetAddress}
      onEditAddress={handleEditAddress}
    />
  );
};
